<script setup lang="ts">
import { GameType, type Game } from '@game/types'
import { vIntersectionObserver } from '@vueuse/components'
import { isEmpty } from 'es-toolkit/compat'

definePageMeta({
    layout: false,
    middleware: [
        'landing-page',
    ],
    layoutBackgroundColor: '#421969'
})

const { site } = useCoreConfig()

const title = `Online Spilleautomater | Spil Progressive Jackpot Spilleautomater hos ${site.title}`
const description = `${site.title} er fyldt med unikke spilleautomater, eksklusive bonusspil og store progressive jackpots. Tilmeld dig nu. Vilkår gælder. 18+. Spil ansvarligt.`

const { signupBundles } = useSignupBundles()
const links = useLinks()

const pageData = await usePageData('allslots')

const { getHead, getSeoMeta } = useStrapiSeo(
    pageData?.seo || {
        metaTitle: title,
        metaDescription: description,
        ogTitle: title,
        ogDescription: description,
    } as any
)

useHead(getHead())
useSeoMeta(getSeoMeta())

// Games
const rootMargin = '100px'
const page_size = 16

const popularGamesLoaded = ref(false)
const { data: popularGames } = useGames({
    params: { page: 1, page_size },
    filter: {
        game_types: [GameType.SLOTS]
    },
    lazy: true,
    key: 'allslots-popular',
    enabled: () => toValue(popularGamesLoaded)
})

function loadPopularGames(entries: IntersectionObserverEntry[]) {
    const isIntersecting = entries[0]?.isIntersecting
    if (!isIntersecting || popularGamesLoaded.value) return

    popularGamesLoaded.value = true
}

// Games with jackpots and Game Jackpots: Get game jackpots and setup the websocket listener
const { gamesJackpots } = useGamesJackpots()

const sortedJackpotSlugs = ref<string[]>([])

whenever(
    () => isEmpty(gamesJackpots?.value) ? undefined : gamesJackpots.value,
    (val) => {
        sortedJackpotSlugs.value = Object.keys(val)
            .sort((a, b) => {
                const valA = val![a]
                const valB = val![b]
                const amountA = valA?.superJackpot ?? valA?.jackpot ?? '0'
                const amountB = valB?.superJackpot ?? valB?.jackpot ?? '0'
                return Number.parseFloat(String(amountB)) - Number.parseFloat(String(amountA))
            })
    },
    { once: true, immediate: true }
)

const jackpotGamesLoaded = ref(false)
const jackpotFilter = computed(() => {
    return {
        slugs: toValue(sortedJackpotSlugs),
        has_jackpot: true
    }
})

const { data: jackpotGames } = useGames({
    params: { page: 1, page_size },
    filter: jackpotFilter,
    lazy: true,
    key: 'allslots-jackpot',
    enabled: () => !!toValue(sortedJackpotSlugs)?.length && toValue(jackpotGamesLoaded)
})

function loadJackpotGames(entries: IntersectionObserverEntry[]) {
    const isIntersecting = entries[0]?.isIntersecting
    if (!isIntersecting || jackpotGamesLoaded.value) return

    jackpotGamesLoaded.value = true
}

const sortedJackpotGames = ref<Game[]>([])

whenever(
    () => jackpotGames?.value?.data?.length ? jackpotGames.value.data : undefined,
    (data) => {
        sortedJackpotGames.value = data.slice().sort((a: Game, b: Game) => {
            const idxA = sortedJackpotSlugs.value.indexOf(a.slug)
            const idxB = sortedJackpotSlugs.value.indexOf(b.slug)

            return idxA - idxB
        })
    },
    { once: true, immediate: true }
)

// Strapi pageData has been disabled for now, as Strapi is not yet seeded
// And Strapi images needs to be optimized with cloudflare props before being used
/* const flags = useFeatureFlags()
const seoName = flags?.enableStrapiLandingPages ? 'allslots' : undefined

const { data: pageData } = await useStrapiPage(seoName, '/landing-pages/{seoName}') */

// NOTE:
// Fallback Content is set in case no Strapi data is available
// (this will be the case until Strapi is seeded and updated on all environments)
const headerContent = {
    gameCards: [
        {
            title: 'Starburst',
            bg: '/game_tiles/evolution/netent/starburst/spilnu/game_tile.png',
            asset: '/media/landing-page/game-cards/starburst-assets.png',
            logo: '/game_tiles/evolution/netent/starburst/spilnu/game_logo.png',
            slug: 'starburst',
            videoId: 'c3a7bbf30b2d402fabd82948e6e4454b',
        },
        {
            title: '12 Masks of Fire Drum Frenzy',
            bg: '/game_tiles/games-global/gameburger-studios/12-masks-of-fire-drum-frenzy/spilnu/game_tile.png',
            asset: '/media/landing-page/game-cards/12-masks-of-fire-drum-frenzy_asset.png',
            logo: '/game_tiles/games-global/gameburger-studios/12-masks-of-fire-drum-frenzy/spilnu/game_logo.png',
            slug: '12-masks-of-fire-drum-frenzy',
            videoId: '1e9d7ddc2fda5f4765984107288390e2',
        },
        {
            title: 'Gold Blitz Ultimate',
            bg: '/game_tiles/games-global/fortune-factory-studios/gold-blitz-ultimate/spilnu/game_tile.png',
            asset: '/media/landing-page/game-cards/gold-blitz-ultimate_asset.png',
            logo: '/game_tiles/games-global/fortune-factory-studios/gold-blitz-ultimate/spilnu/game_logo.png',
            slug: 'gold-blitz-ultimate',
            videoId: '9939217e7538a708c24238fa8d8c387f',
        },
        {
            title: 'Lightning Roulette',
            bg: '/game_tiles/evolution/evolution/lightning-roulette/spilnu/game_tile.png',
            asset: '/media/landing-page/game-cards/lightning-roulette-assets.png',
            logo: '/game_tiles/evolution/evolution/lightning-roulette/spilnu/game_logo.png',
            slug: 'lightning-roulette',
            videoId: '33476edb2a4e5312ca6d850f46285e89',
        }
    ]
}

const videoId = '5eafee12c3c06bf21040ce830b8737d9'
</script>

<template>
    <div class="landing-page landing-page--b">
        <UIApp>
            <LandingPageNavBar fallback-path="/lobby" />

            <main
                class="landing-page__main"
                data-test="welcome-page"
            >
                <LandingPageHeader :content="headerContent as any" :offers="signupBundles" />
                <StickyTerms
                    title="Vigtige Betingelser"
                    is-landing-page
                    :sticky-config="{ container: '.landing-page', minHeight: 48, offset: 50, backgroundColor: '#041135' }"
                />
                <LandingPageUspBar />
                <section class="landing-page__latest-winners container container--lg">
                    <LazyWinnersSidebar
                        :flipped-order="true"
                        :horizontal="true"
                        title="Vindere lige nu"
                        fade-background="#041135"
                    />
                </section>
                <LazyVisibleLandingPageFullscreenVideo :video-id="videoId" hydrate-on-visible />

                <section
                    v-intersection-observer="[loadPopularGames, { rootMargin }]"
                    class="landing-page__games container container--lg"
                >
                    <GameList
                        title="Vores håndplukkede favoritter 🍒"
                        category="POPULAR"
                        :games="popularGames?.data"
                        :trim-dangling-tiles="true"
                        :show-all-button="true"
                        :show-all-link="links.signup.props()"
                        :show-all-text="`🎰 Opdag 500+ Spilleautomater`"
                        :video-enabled="true"
                        @game-click="game => trackGameOpen('gametile', game)"
                    />
                </section>
                <LazyVisibleLandingPageProviders hydrate-on-visible />
                <section
                    v-intersection-observer="[loadJackpotGames, { rootMargin }]"
                    class="landing-page__games container container--lg"
                >
                    <GameList
                        title="EKSKLUSIVE ONLINE SPILLEAUTOMATER"
                        :games="sortedJackpotGames"
                        category="JACKPOT"
                        :trim-dangling-tiles="true"
                        :video-enabled="true"
                        @game-click="game => trackGameOpen('gametile', game)"
                    />
                </section>
            </main>

            <LazyVisibleNimbusStrapiDynamicRenderer
                v-if="!isEmpty(pageData?.dynamicContent)"
                :content="pageData?.dynamicContent"
                :max-width="1024"
                hydrate-on-visible
            />
            <LazyVisibleFooterContainer :enable-terms="false" hydrate-on-visible />
        </UIApp>
    </div>
</template>

<style scoped lang="scss">
.landing-page {
    --sticky-terms-color: #fff;

    &__main {
        a:not(.btn--cta) {
            color: inherit;
        }
    }

    &__games {
        position: relative;
        max-width: 1024px;
        width: 100%;
        padding: 0 15px;
        margin: 0 auto;
        z-index: 4;
    }

    &__latest-winners {
        padding: 0 15px;

        // Temp hack to prevent latest winner loader popping frame - fix in game pack laters
        @media (max-width: #{$screen-md - 1}) {
            :deep(.loader) {
                height: 20px;
                transform: translateY(-5px);
            }
        }
    }

    :deep(.skeleton-list__headline),
    :deep(.game-section h1) {
        text-align: center;
        margin-bottom: 30px;

        font-size: 8.88vw;
        font-size: clamp(28px, 8.88vw, 34px);
        font-weight: 900;

        @media (min-width: $screen-sm-md) {
            br {
                display: none;
            }
        }

        @media (min-width: $screen-md) {
            font-size: 38px;
        }
    }

    :deep(.game-list__section) {
        transform: translateY(-30px);
    }

    :deep(.game-list__title) {
        font-size: 36px;
        font-weight: 900;
        text-align: center;

        @media (min-width: $screen-xs) {
            font-size: 42px;
        }

        @media (min-width: $screen-md) {
            font-size: 46px;
            margin-bottom: 20px;
        }
    }
}

.dynamic-content {
    padding: 0 15px;

    > :deep(div:not(.panel__body)) {
        padding: 40px 0;

        @media (min-width: $screen-lg) {
            padding: 50px 0;
        }
    }

    :deep(.media-text__content) {
        text-align: center;

        @include tablet(md) {
            text-align: left;
        }
    }
}

.footer {
    max-width: 1024px;
    margin: 0 auto;
    padding: 30px 15px;

    @media (min-width: $screen-xl) {
        max-width: $screen-xl;
        padding: 30px;
    }
}
</style>
